import { Injectable } from '@angular/core';
import { Allocation } from '../models/allocation.model';

export interface AvailablePercentageInfo {
  employeeId: string;
  employeeName: string;
  week1Available: number;
  week2Available: number;
  week3Available: number;
  week4Available: number;
  week5Available: number;
  totalAllocated: number;
  averageAllocation: number;
  isFullyAllocated: boolean;
}

export interface WeeklyAllocationSummary {
  week: number;
  allocated: number;
  available: number;
  utilizationPercentage: number;
}

@Injectable({
  providedIn: 'root'
})
export class AllocationPredictorService {

  constructor() { }

  /**
   * Calculate available percentage for an employee for a specific month/year
   * @param employeeId - The employee ID
   * @param allocations - All allocations for the month
   * @param excludeAllocationId - Optional allocation ID to exclude (for edit mode)
   * @returns Available percentage info for each week
   */
  calculateAvailablePercentage(
    employeeId: string,
    allocations: Allocation[],
    excludeAllocationId?: string
  ): AvailablePercentageInfo {
    // Filter allocations for this employee, excluding the current one if in edit mode
    // and excluding synthetic "Available" placeholder rows (those are not real project allocations)
    const employeeAllocations = allocations.filter(a =>
      a.employeeId === employeeId &&
      (!excludeAllocationId || a.id !== excludeAllocationId) &&
      (a.projectName ?? '').toLowerCase() !== 'available'
    );

    // Get employee name - try from filtered allocations first, then from all allocations if needed
    let employeeName = '';
    if (employeeAllocations.length > 0) {
      employeeName = employeeAllocations[0].employeeName;
    } else {
      // If no other allocations found, try to get name from the excluded allocation (edit mode)
      const excludedAllocation = excludeAllocationId 
        ? allocations.find(a => a.id === excludeAllocationId && a.employeeId === employeeId)
        : null;
      if (excludedAllocation) {
        employeeName = excludedAllocation.employeeName;
      } else {
        // Fallback: find any allocation for this employee
        const anyAllocation = allocations.find(a => a.employeeId === employeeId);
        if (anyAllocation) {
          employeeName = anyAllocation.employeeName;
        }
      }
    }

    // Calculate total allocated for each week
    const week1Allocated = employeeAllocations.reduce((sum, a) => sum + (a.week1Percentage || 0), 0);
    const week2Allocated = employeeAllocations.reduce((sum, a) => sum + (a.week2Percentage || 0), 0);
    const week3Allocated = employeeAllocations.reduce((sum, a) => sum + (a.week3Percentage || 0), 0);
    const week4Allocated = employeeAllocations.reduce((sum, a) => sum + (a.week4Percentage || 0), 0);
    const week5Allocated = employeeAllocations.reduce((sum, a) => sum + (a.week5Percentage || 0), 0);

    // Calculate available (100% - allocated) - allows negative values for over-allocation
    const week1Available = 100 - week1Allocated;
    const week2Available = 100 - week2Allocated;
    const week3Available = 100 - week3Allocated;
    const week4Available = 100 - week4Allocated;
    const week5Available = 100 - week5Allocated;

    // Calculate totals
    const totalAllocated = week1Allocated + week2Allocated + week3Allocated + week4Allocated + week5Allocated;
    const averageAllocation = totalAllocated / 5;
    const isFullyAllocated = week1Allocated >= 100 && week2Allocated >= 100 && 
                             week3Allocated >= 100 && week4Allocated >= 100 && 
                             week5Allocated >= 100;

    return {
      employeeId,
      employeeName,
      week1Available,
      week2Available,
      week3Available,
      week4Available,
      week5Available,
      totalAllocated,
      averageAllocation,
      isFullyAllocated
    };
  }

  /**
   * Get weekly allocation summary for an employee
   */
  getWeeklySummary(availableInfo: AvailablePercentageInfo): WeeklyAllocationSummary[] {
    return [
      {
        week: 1,
        allocated: 100 - availableInfo.week1Available,
        available: availableInfo.week1Available,
        utilizationPercentage: 100 - availableInfo.week1Available
      },
      {
        week: 2,
        allocated: 100 - availableInfo.week2Available,
        available: availableInfo.week2Available,
        utilizationPercentage: 100 - availableInfo.week2Available
      },
      {
        week: 3,
        allocated: 100 - availableInfo.week3Available,
        available: availableInfo.week3Available,
        utilizationPercentage: 100 - availableInfo.week3Available
      },
      {
        week: 4,
        allocated: 100 - availableInfo.week4Available,
        available: availableInfo.week4Available,
        utilizationPercentage: 100 - availableInfo.week4Available
      },
      {
        week: 5,
        allocated: 100 - availableInfo.week5Available,
        available: availableInfo.week5Available,
        utilizationPercentage: 100 - availableInfo.week5Available
      }
    ];
  }

  /**
   * Get suggested allocation based on available percentage
   */
  getSuggestedAllocation(availableInfo: AvailablePercentageInfo): number[] {
    return [
      availableInfo.week1Available,
      availableInfo.week2Available,
      availableInfo.week3Available,
      availableInfo.week4Available,
      availableInfo.week5Available
    ];
  }
}

